import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { submissionAPI, requirementAPI } from '../utils/api';
import usePersistentState from '../utils/usePersistentState';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Spinner, EmptyState, Input, Select, SearchableSelect } from '../components/UI';
import { fmtExp, fmtLPA, fmtTimeRange } from '../utils/format';
import RoundRsvp from '../components/RoundRsvp';
import RecordingButton from '../components/RecordingButton';
import InterviewRoundModal from '../components/InterviewRoundModal';
import CandidateDetailsModal from '../components/CandidateDetailsModal';
import StatusUpdateModal from '../components/StatusUpdateModal';

// ── PHASE 3: theme-aware palette ──────────────────────────────────────────────
function useInterviewPalette() {
  const { theme } = useTheme();
  const light = theme === 'light';
  return {
    indigoAccent:  light ? '#4338CA' : '#a5b4fc',
    violetAccent:  light ? '#6D28D9' : '#c4b5fd',
    cyanAccent:    light ? '#0891B2' : '#67e8f9',
    emeraldAccent: light ? '#059669' : '#34d399',
    mutedText:     light ? '#64748B' : '#94a3b8',
    faintText:     light ? '#94A3B8' : '#64748b',
    cardGradient:  light ? '#FFFFFF' : 'linear-gradient(145deg, rgba(15,23,42,0.9) 0%, rgba(7,13,26,0.95) 100%)',
    cardBorder:    light ? '#E2E8F0' : 'rgba(99,102,241,0.15)',
    cardShadow:    light ? '0 1px 3px rgba(15,23,42,0.08)' : '0 4px 24px rgba(0,0,0,0.3)',
    dateRowBg:     light ? '#EEF2FF' : 'rgba(99,102,241,0.07)',
    dateRowBorder: light ? '#C7D2FE' : 'rgba(99,102,241,0.15)',
    dateRowText:   light ? '#3730A3' : '#c7d2fe',
    hrLine:        light ? '#E2E8F0' : 'rgba(255,255,255,0.05)',
    countPillBg:   light ? '#F1F5F9' : 'rgba(255,255,255,0.06)',
    countPillText: light ? '#475569' : '#94a3b8',
    divBgNeutral:  light ? '#F8FAFC'   : 'rgba(255,255,255,0.03)',
    divBorderNeutral: light ? '#E2E8F0' : 'rgba(255,255,255,0.06)',
    toggleOffBg:   light ? '#E2E8F0' : 'rgba(255,255,255,0.08)',
    toggleOffBorder: light ? '#CBD5E1' : 'rgba(255,255,255,0.1)',
    textPrimary:   light ? '#0f172a' : '#f1f5f9',
    textSecondary: light ? '#475569' : '#94a3b8',
    textMuted:     light ? '#94a3b8' : '#64748b',
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const MODE_META = {
  Online:      { icon: '💻', label: 'Online' },
  'In-Person': { icon: '🏢', label: 'In-Person' },
  Phone:       { icon: '📞', label: 'Phone' },
};

const RESULT_STYLE = {
  Pass:    { bg: 'rgba(16,185,129,0.12)', color: '#10b981', border: 'rgba(16,185,129,0.3)' },
  Fail:    { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', border: 'rgba(239,68,68,0.3)'  },
  Pending: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
};

// Today as YYYY-MM-DD in the USER'S LOCAL timezone — deliberately not
// toISOString().slice(0,10), which returns the UTC date. In IST (UTC+5:30)
// local midnight is 18:30 UTC the day before, so that gave yesterday's date.
// Round dates are stored as plain local YYYY-MM-DD strings, so compare to this.
const todayLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function formatDateShort(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// When the invitation email actually went out — "11 Jun, 4:12 pm".
// `inviteSentAt` is stamped server-side by sendInterviewEmail once the
// candidate's email is accepted, so its presence is proof of a real send, not
// an intention. Rounds scheduled without an email (or before this was tracked)
// simply have no value.
function formatSentAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function dayLabel(dateStr) {
  const today = new Date(); today.setHours(0,0,0,0);
  const d     = new Date(dateStr);
  const diff  = Math.round((d - today) / 86400000);
  if (diff === 0)  return { label: 'Today',     color: 'text-indigo-400', bg: 'bg-indigo-500/15 border-indigo-400/30' };
  if (diff === 1)  return { label: 'Tomorrow',  color: 'text-violet-400', bg: 'bg-violet-500/15 border-violet-400/30' };
  if (diff === -1) return { label: 'Yesterday', color: 'text-slate-400',  bg: 'bg-slate-600/20 border-slate-500/30'   };
  if (diff < 0)    return { label: `${Math.abs(diff)}d ago`, color: 'text-slate-500', bg: 'bg-slate-700/20 border-slate-600/30' };
  if (diff <= 7)   return { label: `In ${diff}d`, color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-400/30' };
  return null;
}

// Returns the earliest upcoming/pending round date for a submission (for grouping)
function submissionGroupDate(sub) {
  const rounds = sub.interviewRounds || [];
  if (rounds.length === 0) return null;
  const today = todayLocal();
  // Prefer next pending/upcoming round
  const upcoming = rounds.filter(r => r.date && r.date >= today && r.result === 'Pending');
  if (upcoming.length > 0) return upcoming.sort((a, b) => a.date.localeCompare(b.date))[0].date;
  // Fall back to most recent round date
  const withDate = rounds.filter(r => r.date).sort((a, b) => a.date.localeCompare(b.date));
  return withDate.length > 0 ? withDate[withDate.length - 1].date : null;
}

// ── Interview Card ─────────────────────────────────────────────────────────────
function InterviewCard({ submission, onEdit, onOpenRound }) {
  const P = useInterviewPalette();
  const { isDeveloper } = useAuth();
  const [showDetails, setShowDetails] = useState(false);
  const rounds = submission.interviewRounds || [];
  const candidate = submission.candidate || {};
  const client = submission.client || {};
  const requirement = submission.requirement || {};
  const groupDate = submissionGroupDate(submission);
  const tag = groupDate ? dayLabel(groupDate) : null;

  // Candidate CTC (current → expected), hidden from the Developer role like
  // the Bill Rate on the submissions tile. Rendered with an arrow (current →
  // expected) to match the submission card, not a hyphen range.
  const curLpa = fmtLPA(candidate.currentSalary);
  const expLpa = fmtLPA(candidate.expectedSalary);
  const hasCtc = curLpa || expLpa;
  // Two separate attributions, shown together on the tile:
  //  • submittedByName — who created the submission (the candidate's owner).
  //  • scheduledByName — who moved it to Interview (most recent round's
  //    server-stamped scheduler; null for legacy rounds that predate tracking).
  // They're often the same person but can differ (e.g. the submitter is away and
  // a colleague schedules the interview), so we surface both.
  const scheduledByName = [...rounds].reverse().find(r => r?.scheduledByName)?.scheduledByName || null;
  const submittedByName = submission.createdByUser?.name || null;

  return (
    <div
      className="rounded-2xl p-5 flex flex-col gap-3 transition-all hover:scale-[1.01]"
      style={{
        background: P.cardGradient,
        border: `1px solid ${P.cardBorder}`,
        boxShadow: P.cardShadow,
      }}
    >
      {/* Top row: avatar + name + tag */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff' }}
          >
            {candidate.fullName?.[0]?.toUpperCase() || '?'}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate" style={{ color: P.textPrimary }}>{candidate.fullName}</p>
            <p className="text-xs truncate" style={{ color: P.textMuted }}>{candidate.email}</p>
          </div>
        </div>
        {tag && (
          <span className={`flex-shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full border ${tag.bg} ${tag.color}`}>
            {tag.label}
          </span>
        )}
      </div>

      {/* Client + Job badges */}
      <div className="flex flex-wrap gap-2">
        {requirement.jobTitle && (
          <span className="text-xs px-2 py-0.5 rounded-md font-medium"
            style={{ background: 'rgba(99,102,241,0.12)', color: P.indigoAccent, border: '1px solid rgba(99,102,241,0.2)' }}>
            📋 {requirement.jobTitle}
          </span>
        )}
        {client.clientName && (
          <span className="text-xs px-2 py-0.5 rounded-md"
            style={{ background: P.countPillBg, color: P.textSecondary, border: `1px solid ${P.cardBorder}` }}>
            🏢 {client.clientName}
          </span>
        )}
      </div>

      {/* Interview rounds */}
      {rounds.length === 0 ? (
        <div
          className="rounded-xl px-4 py-3 flex items-center gap-2"
          style={{ background: P.dateRowBg, border: `1px solid ${P.dateRowBorder}` }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={P.indigoAccent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span className="text-sm italic" style={{ color: P.textMuted }}>No date set</span>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {rounds.map((r, idx) => {
            const res = r.result || 'Pending';
            const rs = RESULT_STYLE[res] || RESULT_STYLE.Pending;
            const modeMeta = MODE_META[r.mode] || { icon: '📅' };
            return (
              <div
                key={idx}
                role="button"
                tabIndex={0}
                aria-label={`Round ${r.round || idx + 1} details`}
                onClick={() => onOpenRound?.(r)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenRound?.(r); }
                }}
                className="rounded-xl px-3 py-2.5 flex flex-col gap-1.5 cursor-pointer transition-shadow"
                style={{ background: P.dateRowBg, border: `1px solid ${P.dateRowBorder}` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(99,102,241,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                      style={{ background: P.countPillBg, color: P.textSecondary }}>
                      R{r.round || idx + 1}
                    </span>
                    {r.title && (
                      <span className="text-xs font-medium truncate" style={{ color: P.textSecondary }}>
                        {r.title}
                      </span>
                    )}
                    {r.date ? (
                      <span className="text-xs shrink-0" style={{ color: P.dateRowText }}>
                        {formatDateShort(r.date)}
                        {r.time && <span> · {fmtTimeRange(r.time, r.endTime)}</span>}
                        {r.mode && <span> · {modeMeta.icon} {r.mode}</span>}
                      </span>
                    ) : (
                      <span className="text-xs italic" style={{ color: P.textMuted }}>No date</span>
                    )}
                  </div>
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0"
                    style={{ background: rs.bg, color: rs.color, border: `1px solid ${rs.border}` }}
                  >
                    {res}
                  </span>
                </div>

                {/* Per-round invite status. Absence of inviteSentAt is
                    meaningful — the candidate was never emailed for this round —
                    so we state it rather than rendering nothing, which would be
                    indistinguishable from "no data". */}
                {/* gap-x separates; nowrap keeps each fact atomic so a narrow
                    column can never split "6 Aug, 3:50 pm" across two lines. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                  {r.inviteSentAt ? (
                    <span className="whitespace-nowrap" style={{ color: P.textSecondary }}>
                      <span style={{ color: P.emeraldAccent }}>✓</span>{' '}
                      Invite sent{' '}
                      <span style={{ fontWeight: 600, color: P.textPrimary }}>
                        {formatSentAt(r.inviteSentAt)}
                      </span>
                    </span>
                  ) : (
                    <span className="whitespace-nowrap" style={{ color: P.textMuted }}>
                      ○ Invite not sent
                    </span>
                  )}
                  {r.meetingLink && (
                    <span className="whitespace-nowrap" style={{ color: P.textMuted }}
                      title="A Teams meeting was created for this round">
                      💻 Teams link
                    </span>
                  )}
                  {/* Only renders once the slot is over and there was a Teams meeting. */}
                  <RecordingButton submissionId={submission.id} round={r} />
                </div>

                <RoundRsvp round={r} />
              </div>
            );
          })}
        </div>
      )}

      {/* Experience + phone */}
      <div className="flex items-center justify-between gap-2 text-xs pt-1 min-w-0" style={{ color: P.textMuted, borderTop: `1px solid ${P.hrLine}` }}>
        <span className="truncate min-w-0 flex-1">
          💼 {fmtExp(candidate.experience) || '—'}
          {candidate.currentRole ? ` · ${candidate.currentRole}` : ''}
          {candidate.currentCompany ? ` @ ${candidate.currentCompany}` : ''}
        </span>
        {candidate.phone && (
          <a href={`tel:${candidate.phone}`} className="truncate min-w-0 flex-shrink" style={{ color: P.textMuted }}>
            📞 {candidate.phone}
          </a>
        )}
      </div>

      {/* Candidate details + scheduler — only rendered when there's something to show */}
      {((!isDeveloper && hasCtc) || candidate.location || (candidate.noticePeriod != null && candidate.noticePeriod !== '') || scheduledByName || submittedByName) && (
        <div className="flex flex-col gap-1 text-xs min-w-0" style={{ color: P.textMuted }}>
          {!isDeveloper && hasCtc && <span className="truncate min-w-0">💰 CTC: {curLpa || '—'} <span style={{ opacity: 0.6 }}>→</span> <span style={{ fontWeight: 600 }}>{expLpa || '—'}</span></span>}
          {candidate.location && <span className="truncate min-w-0">📍 {candidate.location}</span>}
          {candidate.noticePeriod != null && candidate.noticePeriod !== '' && (
            <span className="truncate min-w-0">⏳ Notice: {candidate.noticePeriod}d{candidate.servingNoticePeriod ? ' · serving' : ''}</span>
          )}
          {/* Show BOTH attributions so it's clear who submitted the candidate
              vs. who moved it to Interview — they can be different people
              (e.g. the submitter is on leave and a colleague schedules it). */}
          {submittedByName && (
            <span className="truncate min-w-0">👤 Submitted by {submittedByName}</span>
          )}
          {scheduledByName && (
            <span className="truncate min-w-0">🗓 Scheduled by {scheduledByName}</span>
          )}
        </div>
      )}

      {/* Update this interview / status — opens the same editor as the Submissions page */}
      {!isDeveloper && onEdit && (
        <button
          type="button"
          onClick={() => onEdit(submission)}
          className="w-full mt-2 px-3 py-2 text-xs font-semibold rounded-lg transition-opacity"
          style={{ background: P.indigoAccent, color: '#ffffff', border: 'none', opacity: 0.95 }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '0.95'; }}
        >
          ✏️ Update Interview
        </button>
      )}

      {/* View full candidate details */}
      {submission.candidateId && (
        <button
          type="button"
          onClick={() => setShowDetails(true)}
          className="w-full mt-1 px-3 py-2 text-xs font-semibold rounded-lg border transition-colors"
          style={{ borderColor: P.cardBorder, color: P.indigoAccent, background: P.divBgNeutral }}
          onMouseEnter={e => { e.currentTarget.style.background = P.dateRowBg; }}
          onMouseLeave={e => { e.currentTarget.style.background = P.divBgNeutral; }}
        >
          View Candidate Details
        </button>
      )}

      {showDetails && (
        <CandidateDetailsModal
          candidateId={submission.candidateId}
          candidateFallback={candidate}
          onClose={() => setShowDetails(false)}
        />
      )}
    </div>
  );
}

// ── Group submissions by their next round date ─────────────────────────────────
function groupByDate(submissions) {
  const noDate   = submissions.filter(s => !submissionGroupDate(s));
  const withDate = submissions.filter(s => submissionGroupDate(s));
  withDate.sort((a, b) => (submissionGroupDate(a) || '').localeCompare(submissionGroupDate(b) || ''));
  const groups = {};
  withDate.forEach(s => {
    const day = submissionGroupDate(s);
    if (!groups[day]) groups[day] = [];
    groups[day].push(s);
  });
  return { groups, noDate };
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function Interviews() {
  const P = useInterviewPalette();

  const [all,          setAll]          = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  // Filters are kept for this tab, so leaving the page (or reloading it) and
  // coming back keeps what the recruiter had set.
  const [search,       setSearch]       = usePersistentState('interviews:search', '');
  const [modeFilter,   setModeFilter]   = usePersistentState('interviews:mode', '');
  const [showPast,     setShowPast]     = usePersistentState('interviews:showPast', false);
  const [requirementFilter, setRequirementFilter] = usePersistentState('interviews:requirement', '');
  // requirementStatus cascades into the Requirement dropdown — selecting
  // Open / On Hold / Closed narrows requirement options AND filters the
  // interview list to submissions whose requirement matches that status.
  const [requirementStatus, setRequirementStatus] = usePersistentState('interviews:requirementStatus', '');
  const [requirements, setRequirements] = useState([]);
  // Submission currently being edited in the shared "Update Status" modal (null = closed).
  const [editingSub, setEditingSub] = useState(null);
  // { round, sub } — the round whose full detail dialog is open.
  const [roundModal, setRoundModal] = useState(null);

  // silent: a background refresh (tab regained focus) swaps the data in place.
  // It must NOT show the spinner — that unmounts the list, closing an open
  // round dialog and any recording playing inside it.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      // Page through every Interview submission. The list is newest-first, so a
      // single page silently dropped the oldest ones — including an old
      // submission whose next round is today — from the groups and every KPI.
      // MAX_PAGES is only a runaway guard; hitting it is reported, not hidden.
      const PAGE_SIZE = 200, MAX_PAGES = 25;
      let rows = [], page = 1, pages = 1, total = 0;
      do {
        const { data } = await submissionAPI.getAll({ status: 'Interview', limit: PAGE_SIZE, page });
        rows = rows.concat(data.submissions || []);
        pages = Number(data.pages) || 1;
        total = Number(data.total) || rows.length;
        page += 1;
      } while (page <= pages && page <= MAX_PAGES);
      // A submission created between page requests shifts later pages by one,
      // so the same row can arrive twice — keep the first copy.
      const seen = new Set();
      rows = rows.filter(s => (seen.has(s.id) ? false : (seen.add(s.id), true)));
      setAll(rows);
      if (pages > MAX_PAGES) {
        toast(`Showing the newest ${rows.length} of ${total} interview submissions.`, { icon: '⚠️', id: 'interviews-truncated' });
      }
    } catch {
      if (!silent) toast.error('Failed to load interviews');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []); // eslint-disable-line

  // Load requirement dropdown once for the filter
  useEffect(() => {
    requirementAPI.dropdown()
      .then(({ data }) => setRequirements(data.requirements || []))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh when the tab regains focus. RSVPs land while you are reading the
  // reply in your mail client, so returning to this tab is exactly the moment
  // the data is stale. Rate-limited so alt-tabbing does not hammer the API, and
  // the interval is above the 30s response cache — below it the refetch would
  // be served from cache and change nothing.
  useEffect(() => {
    let last = Date.now();
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - last < 31_000) return;
      last = Date.now();
      load({ silent: true });
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const today = new Date(); today.setHours(0,0,0,0);   // local midnight, for the day headers below
  const todayStr = todayLocal();

  const filtered = all.filter(s => {
    const c   = s.candidate || {};
    const cl  = s.client    || {};
    const req = s.requirement || {};
    const matchSearch = !search.trim() ||
      c.fullName?.toLowerCase().includes(search.toLowerCase()) ||
      cl.clientName?.toLowerCase().includes(search.toLowerCase()) ||
      req.jobTitle?.toLowerCase().includes(search.toLowerCase());
    const rounds = s.interviewRounds || [];
    const matchMode = !modeFilter || rounds.some(r => r.mode === modeFilter);
    const groupDate = submissionGroupDate(s);
    const isPast = groupDate && groupDate < todayStr;
    const matchPast = showPast ? true : !isPast;
    const matchReq = !requirementFilter || s.requirementId === requirementFilter;
    // When a specific requirement is picked, that wins — status is implied.
    // Otherwise, filter by the requirement's status (looked up via the
    // requirements dropdown list since interview rows only carry req IDs).
    const matchReqStatus = !requirementStatus || requirementFilter
      || (requirements.find(r => r.id === s.requirementId)?.status === requirementStatus);
    return matchSearch && matchMode && matchPast && matchReq && matchReqStatus;
  });

  const { groups, noDate } = groupByDate(filtered);
  const sortedDays = Object.keys(groups).sort();

  // KPI counts
  const upcomingCount = all.filter(s => {
    const gd = submissionGroupDate(s);
    return !gd || gd >= todayStr;
  }).length;
  const todayCount = all.filter(s => submissionGroupDate(s) === todayStr).length;
  const onlineCount = all.filter(s => (s.interviewRounds || []).some(r => r.mode === 'Online')).length;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: P.textPrimary }}>Upcoming Interviews</h1>
          <p className="text-sm mt-1" style={{ color: P.mutedText }}>
            {upcomingCount} upcoming
            {todayCount > 0 && <span style={{ color: P.indigoAccent, fontWeight: 600 }}> · {todayCount} today</span>}
          </p>
        </div>
        <button
          onClick={async () => { setRefreshing(true); await load(); setRefreshing(false); }}
          disabled={refreshing}
          className="flex items-center gap-2 text-sm font-medium px-3.5 py-2 rounded-xl transition-all disabled:opacity-60"
          style={{ background: 'rgba(99,102,241,0.12)', color: P.indigoAccent, border: '1px solid rgba(99,102,241,0.25)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={refreshing ? { animation: 'spin 1s linear infinite' } : {}}>
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* ── KPI strip ── */}
      {!loading && all.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Scheduled', value: all.length,     color: P.indigoAccent,  bg: 'rgba(99,102,241,0.1)',  border: 'rgba(99,102,241,0.2)'  },
            { label: 'Today',           value: todayCount,     color: P.emeraldAccent, bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.2)'  },
            { label: 'Upcoming',        value: upcomingCount,  color: P.violetAccent,  bg: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.2)'  },
            { label: 'Online',          value: onlineCount,    color: P.cyanAccent,    bg: 'rgba(6,182,212,0.08)',  border: 'rgba(6,182,212,0.2)'   },
          ].map(k => (
            <div key={k.label} className="rounded-xl px-4 py-3" style={{ background: k.bg, border: `1px solid ${k.border}` }}>
              <p className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</p>
              <p className="text-xs mt-0.5" style={{ color: P.mutedText }}>{k.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-3 items-center">
        <Input placeholder="Search candidate, company, role…" value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-auto sm:max-w-xs" />
        <Select value={modeFilter} onChange={e => setModeFilter(e.target.value)} className="w-full sm:w-36">
          <option value="">All Modes</option>
          <option>Online</option>
          <option>In-Person</option>
          <option>Phone</option>
        </Select>
        {/* Requirement Status — cascades into the Requirement dropdown.
            Selecting a status clears the picked requirement so users
            don't end up with a stale selection that no longer matches. */}
        <SearchableSelect
          value={requirementStatus}
          onChange={(val) => { setRequirementStatus(val); setRequirementFilter(''); }}
          placeholder="All Req Statuses"
          className="w-full sm:w-44"
          options={[
            { value: 'Open',    label: 'Open'    },
            { value: 'On Hold', label: 'On Hold' },
            { value: 'Closed',  label: 'Closed'  },
          ]}
        />
        <SearchableSelect
          value={requirementFilter}
          onChange={setRequirementFilter}
          placeholder="All Requirements"
          className="w-full sm:w-52"
          options={requirements
            .filter(r => !requirementStatus || r.status === requirementStatus)
            .map(r => ({ value: r.id, label: r.jobTitle, sub: r.client?.clientName || '' }))}
        />
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <div
            onClick={() => setShowPast(v => !v)}
            className="relative w-9 h-5 rounded-full transition-all"
            style={{ background: showPast ? '#6366f1' : P.toggleOffBg, border: `1px solid ${P.toggleOffBorder}` }}
          >
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showPast ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
          <span className="text-sm" style={{ color: P.mutedText }}>Show past</span>
        </label>
      </div>

      {/* ── Content ── */}
      {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : filtered.length === 0 ? (
        <EmptyState
          icon="📅"
          title="No interviews scheduled"
          message={all.length > 0 ? 'Try adjusting your filters.' : 'Move a submission to "Interview" status and add rounds to see them here.'}
        />
      ) : (
        <div className="space-y-8">
          {sortedDays.map(day => {
            const d    = new Date(day);
            const diff = Math.round((d - today) / 86400000);
            const isToday    = diff === 0;
            const isTomorrow = diff === 1;
            const isPast     = diff < 0;
            const headerLabel = isToday ? 'Today'
              : isTomorrow ? 'Tomorrow'
              : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            const headerColor = isToday ? P.indigoAccent : isTomorrow ? P.violetAccent : isPast ? P.faintText : P.mutedText;

            return (
              <div key={day}>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                    style={{ background: isToday ? 'rgba(99,102,241,0.12)' : P.divBgNeutral, border: `1px solid ${isToday ? 'rgba(99,102,241,0.3)' : P.divBorderNeutral}` }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={headerColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    <span className="text-sm font-semibold" style={{ color: headerColor }}>{headerLabel}</span>
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded-full" style={{ background: P.countPillBg, color: P.countPillText }}>
                      {groups[day].length}
                    </span>
                  </div>
                  <div className="flex-1 h-px" style={{ background: P.hrLine }} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {groups[day].map(s => <InterviewCard key={s.id} submission={s} onEdit={setEditingSub} onOpenRound={(r) => setRoundModal({ round: r, sub: s })} />)}
                </div>
              </div>
            );
          })}

          {noDate.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: P.divBgNeutral, border: `1px solid ${P.divBorderNeutral}` }}>
                  <span className="text-sm font-semibold" style={{ color: P.mutedText }}>No Date Set</span>
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded-full" style={{ background: P.countPillBg, color: P.countPillText }}>{noDate.length}</span>
                </div>
                <div className="flex-1 h-px" style={{ background: P.hrLine }} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {noDate.map(s => <InterviewCard key={s.id} submission={s} onEdit={setEditingSub} onOpenRound={(r) => setRoundModal({ round: r, sub: s })} />)}
              </div>
            </div>
          )}
        </div>
      )}

      <StatusUpdateModal
        submission={editingSub}
        isOpen={!!editingSub}
        onClose={() => setEditingSub(null)}
        onSaved={load}
      />

      <InterviewRoundModal
        isOpen={!!roundModal}
        onClose={() => setRoundModal(null)}
        round={roundModal?.round}
        context={{
          submissionId:     roundModal?.sub?.id,
          candidateName:    roundModal?.sub?.candidate?.fullName,
          candidateEmail:   roundModal?.sub?.candidate?.email,
          clientName:       roundModal?.sub?.client?.clientName,
          requirementTitle: roundModal?.sub?.requirement?.jobTitle,
        }}
      />
    </div>
  );
}
